import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Download, FileText, RefreshCw, Search, Send, Users, X } from 'lucide-react';
import api from 'services/api';
import { usePermissions } from 'hooks/usePermissions';
import Modal from 'components/common/Modal';
import MultiSelectFilter from 'components/common/MultiSelectFilter';
import Pagination from 'components/Pagination';
import { nomeParaCena } from 'features/comercial/cenas/eventosDoRanking';
import { nomeDoAnexo } from 'features/comercial/services/comissaoReguaService';
import { mesCorrente } from 'features/comercial/components/comissao/ComissaoReguaWorkspace';
import ReguaDaCobranca from './ReguaDaCobranca';
import SerieDaComissao, { GRUPOS } from './SerieDaComissao';

/**
 * Comissão da cobrança interna da MOV: quanto o time gerou no mês, quem gerou, e por que cada
 * recebimento em atraso gerou ou não gerou comissão.
 *
 * Nada é calculado aqui. O backend lê o IXC (`ComissaoCobrancaService`) e já devolve a situação de
 * cada recebimento; a tela só recorta e mostra. As regras estão em `docs/COBRANCA_COMISSAO.md`.
 */

const PRIMEIRO_MES = '2026-09';
const POR_PAGINA = 25;

const SITUACAO = {
  COMISSIONADO: { curto: 'Lançada', tom: 'bom', explica: 'A comissão já está lançada no IXC, com o mesmo valor calculado.' },
  A_LANCAR: { curto: 'A lançar', tom: 'atencao', explica: 'A comissão está apurada, mas ainda não foi lançada no IXC.' },
  COMISSAO_DIVERGENTE: { curto: 'Valor divergente', tom: 'perigo', explica: 'O IXC tem comissão lançada nesta OS para esta pessoa, mas o valor não bate com o calculado. Confira o lançamento.' },
  FORA_JANELA: { curto: 'Sem OS de cobrança', explica: 'Quando o cliente pagou, não havia OS de cobrança interna aberta para este contrato: o pagamento não passou pela cobrança.' },
  COBRANCA_EXTERNA: { curto: 'Cobrança externa', explica: 'O pagamento caiu com a cobrança externa aberta, e nesse caso a comissão interna não se aplica.' },
  NAO_ASSUMIDA: { curto: 'Não assumida', explica: 'O cliente pagou antes de o cobrador assumir a OS de cobrança: a OS estava só encaminhada pela distribuição automática, e a cobrança ainda não tinha começado.' },
  OS_NAO_FINALIZADA: { curto: 'OS não finalizada', explica: 'A OS de cobrança continua aberta. A comissão vai para quem finalizá-la.' },
  FINALIZADOR_NAO_COBRADOR: { curto: 'Não é da cobrança', explica: 'Quem finalizou a OS não está no time de cobrança. Se a pessoa cobra, o cadastro dela no IXC precisa terminar com "(COB - INT)".' },
  CONTRATO_INCONSISTENTE: { curto: 'Contrato não confere', explica: 'O contrato da OS de cobrança não confere com o do boleto, ou a renegociação junta contratos diferentes. Precisa de conferência manual.' },
  VENCEU_DEPOIS_DA_OS: { curto: 'Venceu após a OS', explica: 'A OS de cobrança aberta no pagamento foi aberta antes deste título vencer, ou seja, por causa de outro título do contrato.' },
  SOBREPOSICAO: { curto: 'OS sobrepostas', explica: 'Havia duas OS de cobrança abertas ao mesmo tempo para o contrato. Precisa de decisão manual sobre quem cobrou.' },
};
const TOM = {
  bom: 'bg-[color:var(--status-good-chip)] text-[color:var(--status-good-strong)]',
  atencao: 'bg-[color:var(--status-warning-chip)] text-[color:var(--status-warning-strong)]',
  perigo: 'bg-[color:var(--status-danger-chip)] text-[color:var(--status-danger-strong)]',
  neutro: 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]',
};

/** As duas versões do arquivo. A resumida é a do pagamento: uma linha por cobrador. */
const MODELOS = [
  { valor: 'completo', titulo: 'Completa', descricao: 'Resumo, cada recebimento que gerou e que não gerou comissão, com o motivo.' },
  { valor: 'resumido', titulo: 'Resumida', descricao: 'Uma linha por cobrador: nome completo, quantidade de recebimentos com comissão e valor da comissão.' },
];

const caixa = 'rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)]';
const campo = 'min-h-11 rounded-md border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]';
const botaoSecundario = `${campo} inline-flex items-center gap-2 font-medium transition hover:bg-[color:var(--surface-muted)] disabled:cursor-not-allowed disabled:opacity-60`;
const foco = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]';

const reais = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dia = (valor) => (valor ? String(valor).slice(0, 10).split('-').reverse().join('/') : '—');
const mesPorExtenso = (mes) => new Date(`${mes}-01T00:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
const pessoa = (nome) => (nome ? nomeParaCena(nome) : '—');
const normalizar = (valor) => String(valor ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('pt-BR').trim();

const Chip = ({ situacao }) => {
  const s = SITUACAO[situacao] || { curto: situacao };
  return <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${TOM[s.tom || 'neutro']}`}>{s.curto}</span>;
};

const ComissaoCobranca = ({ refreshKey = 0 }) => {
  const [mes, setMes] = useState(mesCorrente);
  const [cobradorId, setCobradorId] = useState(null);
  const { hasPermission } = usePermissions();
  const [confirmar, setConfirmar] = useState(false);
  const [marcados, setMarcados] = useState([]);
  const [lancando, setLancando] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [baixando, setBaixando] = useState('');
  // Formato que abriu o modal de download ('xlsx' | 'pdf'), e o modelo escolhido nele.
  const [pedirModelo, setPedirModelo] = useState(null);
  const [modelo, setModelo] = useState('completo');
  const [aba, setAba] = useState('gerou');
  const [motivo, setMotivo] = useState(null);
  const [grupo, setGrupo] = useState(null);
  const [busca, setBusca] = useState('');
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA);
  const listaRef = useRef(null);
  const [aberta, setAberta] = useState(null);
  // O time inteiro chega na primeira leitura; filtrar um cobrador não pode esvaziar o seletor.
  const [equipe, setEquipe] = useState([]);

  const requisicaoAtual = useRef(0);
  const carregar = useCallback(async (atualizar = false) => {
    const requisicao = ++requisicaoAtual.current;
    setCarregando(true);
    setErro('');
    try {
      const resposta = await api.get(`/cobranca/comissao/${mes}`, {
        params: { ...(cobradorId ? { cobrador: cobradorId } : {}), ...(atualizar ? { atualizar: 1 } : {}) },
      });
      if (requisicao !== requisicaoAtual.current) return;
      const novos = resposta.data?.data;
      setDados(novos);
      if (!cobradorId) setEquipe(novos?.cobradores || []);
    } catch (falha) {
      if (requisicao !== requisicaoAtual.current) return;
      setDados(null);
      setErro(falha.response?.data?.message || 'Não foi possível carregar a comissão de cobrança.');
    } finally {
      if (requisicao === requisicaoAtual.current) setCarregando(false);
    }
  }, [mes, cobradorId]);

  useEffect(() => { carregar(); }, [carregar]);
  // O botão de atualizar do cabeçalho da página relê o IXC — só quando é clicado, não na montagem.
  const ultimoRefresh = useRef(refreshKey);
  useEffect(() => {
    if (ultimoRefresh.current === refreshKey) return;
    ultimoRefresh.current = refreshKey;
    carregar(true);
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setMotivo(null); }, [aba, mes, cobradorId]);
  // Qualquer mudança no que está listado volta para a primeira página.
  useEffect(() => { setPagina(1); }, [aba, mes, cobradorId, motivo, busca, porPagina, grupo]);

  const baixar = async (formato, modeloEscolhido) => {
    setPedirModelo(null);
    setBaixando(formato);
    setErro('');
    try {
      const resposta = await api.get(`/cobranca/comissao/${mes}/exportar/${formato}`, {
        params: { ...(cobradorId ? { cobrador: cobradorId } : {}), modelo: modeloEscolhido }, responseType: 'blob',
      });
      const url = URL.createObjectURL(resposta.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = nomeDoAnexo(resposta.headers?.['content-disposition'], `comissao-cobranca-${mes}${modeloEscolhido === 'resumido' ? '-resumida' : ''}.${formato}`);
      document.body.appendChild(link); link.click(); link.remove();
      // Revogar na hora cancela o download em alguns navegadores; o quadro seguinte já é seguro.
      requestAnimationFrame(() => URL.revokeObjectURL(url));
    } catch {
      setErro('Não foi possível baixar o arquivo.');
    } finally {
      setBaixando('');
    }
  };

  // O servidor recalcula o que lançar; a tela só repete enquanto houver lote e ele avançar.
  const lancar = async () => {
    setLancando({ lancadas: 0, valor: 0 });
    setResultado(null);
    setErro('');
    const total = { lancadas: 0, ja_lancadas: 0, valor: 0, recusadas: [], indeterminadas: [], restantes: 0 };
    try {
      for (;;) {
        const r = await api.post(`/cobranca/comissao/${mes}/lancar`, null, { params: { cobrador: marcados.join(',') } });
        const lote = r.data?.data || {};
        total.lancadas += lote.lancadas || 0;
        total.ja_lancadas = Math.max(total.ja_lancadas, lote.ja_lancadas || 0);
        total.valor += lote.valor_lancado || 0;
        total.recusadas = lote.recusadas || [];
        total.indeterminadas = lote.indeterminadas || [];
        total.restantes = lote.restantes || 0;
        setLancando({ lancadas: total.lancadas, valor: total.valor });
        if (!total.restantes || !lote.lancadas) break;
      }
      setResultado(total);
    } catch (falha) {
      setResultado(total);
      setErro(falha.response?.data?.message || 'O lançamento parou no meio. Confira o resultado antes de repetir.');
    } finally {
      setLancando(null);
      setConfirmar(false);
      carregar();
    }
  };

  const passiveis = (dados?.cobradores || []).filter((c) => c.comissao_a_lancar > 0);
  const totalMarcado = passiveis.filter((c) => marcados.includes(c.id)).reduce((t, c) => t + c.comissao_a_lancar, 0);
  const alternar = (id) => setMarcados((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));
  const abrirLancamento = () => { setMarcados(passiveis.map((c) => c.id)); setConfirmar(true); };

  const resumo = dados?.resumo;
  const linhas = useMemo(() => dados?.linhas || [], [dados]);
  // Mesma classificação de ComissaoCobrancaService::recuperacao.
  const doGrupo = useMemo(() => linhas.filter((l) => !grupo || (
    l.situacao === 'COBRANCA_EXTERNA' ? 'externa'
      : ['FORA_JANELA', 'VENCEU_DEPOIS_DA_OS'].includes(l.situacao) ? 'apos_os' : 'interna'
  ) === grupo), [linhas, grupo]);
  const daAba = useMemo(() => doGrupo.filter((l) => aba === 'todos' || l.gerou === (aba === 'gerou')), [doGrupo, aba]);
  const escolherGrupo = (chave) => {
    setGrupo(chave);
    setAba('todos');
    setMotivo(null);
    setBusca('');
    listaRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  };
  const visiveis = useMemo(() => {
    const termo = normalizar(busca);
    return daAba
      .filter((l) => !motivo || l.situacao === motivo)
      .filter((l) => !termo || (/^\d+$/.test(termo)
        ? [l.cliente_id, l.contrato_id, l.os_id, l.boleto_id].some((v) => String(v ?? '') === termo)
        : normalizar(l.cliente).includes(termo)))
      .sort((a, b) => String(b.baixa).localeCompare(String(a.baixa)));
  }, [daAba, motivo, busca]);
  const opcoesCobrador = useMemo(() => [
    { id: 0, label: 'Time todo' },
    ...equipe.map((c) => ({ id: c.id, label: pessoa(c.nome) })),
  ], [equipe]);

  return (
    <>
      <div className="space-y-5">
        <div className="sticky top-[var(--header-shell-height)] z-40 flex flex-wrap items-end gap-3 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-strong)] p-3 sm:p-4">
          <label className="flex min-w-0 flex-col gap-1 text-xs font-medium text-[color:var(--text-muted)]">
            Mês do pagamento
            <input type="month" value={mes} min={PRIMEIRO_MES} max={mesCorrente()} onChange={(e) => { if (e.target.value) { setMes(e.target.value); setCobradorId(null); } }} className={campo} />
          </label>
          <div className="flex w-full flex-col gap-1 text-xs font-medium text-[color:var(--text-muted)] sm:w-64">
            <label htmlFor="filtro-cobradores">Cobradores</label>
            <MultiSelectFilter
              options={opcoesCobrador.filter((o) => o.id).map((o) => `${o.label} · ${o.id}`)}
              values={opcoesCobrador.filter((o) => String(cobradorId || '').split(',').includes(String(o.id))).map((o) => `${o.label} · ${o.id}`)}
              onChange={(nomes) => {
                const ids = nomes.map((nome) => Number(nome.split(' · ').pop())).sort((a, b) => a - b);
                setCobradorId(ids.length === 1 ? ids[0] : ids.join(',') || null);
              }}
              placeholder="Time todo"
              label="Cobradores"
              buttonId="filtro-cobradores"
            />
          </div>
          <button type="button" onClick={() => carregar(true)} disabled={carregando} className={botaoSecundario}>
            <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin motion-reduce:animate-none' : ''}`} aria-hidden="true" />
            {carregando ? 'Atualizando' : 'Atualizar'}
          </button>
          <div className="flex gap-2 sm:ml-auto">
            <button type="button" onClick={() => setPedirModelo('xlsx')} disabled={!dados || Boolean(baixando)} className={botaoSecundario}>
              <Download className="h-4 w-4" aria-hidden="true" />
              {baixando === 'xlsx' ? 'Gerando planilha' : 'Baixar XLSX'}
            </button>
            <button type="button" onClick={() => setPedirModelo('pdf')} disabled={!dados || Boolean(baixando)} className={botaoSecundario}>
              <FileText className="h-4 w-4" aria-hidden="true" />
              {baixando === 'pdf' ? 'Gerando relatório' : 'Baixar PDF'}
            </button>
            {hasPermission('cobranca.comissao.lancar') && (dados?.resumo?.comissao_a_lancar || 0) > 0 && (
              <button type="button" onClick={abrirLancamento} disabled={Boolean(lancando)} className={botaoSecundario}>
                <Send className="h-4 w-4" aria-hidden="true" />
                Lançar no IXC
              </button>
            )}
          </div>
        </div>

        {resultado && (
        <p role="status" className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-muted)] p-3 text-sm text-[color:var(--text-primary)]">
          {resultado.lancadas} {resultado.lancadas === 1 ? 'comissão lançada' : 'comissões lançadas'} ({reais(resultado.valor)}).
          {resultado.recusadas.length > 0 && ` ${resultado.recusadas.length} recusadas pela conferência da OS: ${resultado.recusadas.map((r) => r.origem).join(', ')}.`}
          {resultado.indeterminadas.length > 0 && ` Sem confirmação, confira no IXC antes de repetir: ${resultado.indeterminadas.join(', ')}.`}
          {resultado.restantes > 0 && ` ${resultado.restantes} ficaram para a próxima tentativa.`}
        </p>
      )}

      {erro && (
          <p role="alert" className="flex items-start gap-2 rounded-lg border border-[color:var(--status-danger-border)] bg-[color:var(--status-danger-chip)] p-3 text-sm text-[color:var(--status-danger-strong)]">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="flex-1">{erro}</span>
            <button type="button" onClick={() => carregar(true)} className="shrink-0 rounded font-semibold underline underline-offset-4 focus:outline-none focus:ring-2 focus:ring-current">Tentar de novo</button>
          </p>
        )}

        {carregando && !dados && <div role="status" aria-label="Carregando a comissão" className="h-48 animate-pulse rounded-xl bg-[color:var(--surface-muted)]" />}

        {resumo && (
          <>
            <Resumo resumo={resumo} mes={mes} titulo={dados.cobrador ? (dados.cobrador.nome || '').split(', ').map(pessoa).join(', ') : 'time de cobrança'} grupo={grupo} onEscolherGrupo={escolherGrupo} />
            <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.65fr)_minmax(22rem,1fr)]">
            <SerieDaComissao mes={mes} cobradorId={cobradorId} resumoAtual={dados.competencia === mes && (dados.cobrador?.id || null) === cobradorId ? resumo : null} primeiroMes={PRIMEIRO_MES} onEscolherMes={setMes} grupoSelecionado={grupo} onEscolherGrupo={escolherGrupo} />

            {dados.cobradores.length > 0 && (
              <Cobradores cobradores={dados.cobradores} selecionado={cobradorId} onEscolher={(id) => setCobradorId((atual) => (String(atual) === String(id) ? null : id))} />
            )}
            </div>
            {dados.cobrador && (
              <button type="button" onClick={() => setCobradorId(null)} className={`${botaoSecundario} text-sm`}>
                <Users className="h-4 w-4" aria-hidden="true" />
                Ver o time todo
              </button>
            )}

            <section ref={listaRef} className={`${caixa} scroll-mt-64 overflow-hidden`} aria-labelledby="titulo-recebimentos">
              <div className="space-y-4 border-b border-[color:var(--border)] p-4">
                {grupo && (
                  <div className="flex flex-wrap items-center justify-between gap-2" role="status">
                    <p className="text-sm text-[color:var(--text-primary)]">
                      {GRUPOS.find((g) => g.chave === grupo).rotulo} · {doGrupo.length} {doGrupo.length === 1 ? 'recebimento' : 'recebimentos'} · {reais(doGrupo.reduce((total, l) => total + l.valor_pago, 0))}
                    </p>
                    <button type="button" onClick={() => setGrupo(null)} className={botaoSecundario}>Limpar filtro de recuperação</button>
                  </div>
                )}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <h3 id="titulo-recebimentos" className="w-full text-base font-semibold text-[color:var(--text-primary)]">Conferir recebimentos</h3>
                  <div role="tablist" aria-label="Recebimentos" className="inline-flex max-w-full flex-wrap rounded-lg bg-[color:var(--surface-muted)] p-1">
                    {[['todos', 'Todos', doGrupo.length], ['gerou', 'Geraram comissão', doGrupo.filter((l) => l.gerou).length], ['nao', 'Não geraram', doGrupo.filter((l) => !l.gerou).length]].map(([id, rotulo, total]) => (
                      <button
                        key={id}
                        type="button"
                        role="tab"
                        aria-selected={aba === id}
                        onClick={() => setAba(id)}
                        className={`min-h-10 rounded-md px-3 text-sm font-medium transition ${foco} ${aba === id ? 'bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-sm' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'}`}
                      >
                        {rotulo} <span className="tabular-nums">({total})</span>
                      </button>
                    ))}
                  </div>
                  <label className="relative w-full sm:w-72">
                    <span className="sr-only">Buscar cliente</span>
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-muted)]" aria-hidden="true" />
                    <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Cliente, ID, contrato ou OS" className={`${campo} w-full pl-9`} />
                  </label>
                </div>
                <div className="flex flex-wrap gap-2" aria-label="Filtrar por situação">
                  {resumo.por_situacao.map((s) => ({ ...s, quantidade: doGrupo.filter((l) => l.situacao === s.situacao).length })).filter((s) => s.quantidade > 0 && (aba === 'todos' || s.gerou === (aba === 'gerou'))).map((s) => (
                    <button
                      key={s.situacao}
                      type="button"
                      aria-pressed={motivo === s.situacao}
                      onClick={() => setMotivo((atual) => (atual === s.situacao ? null : s.situacao))}
                      className={`inline-flex min-h-9 items-center gap-2 rounded-full border px-3 text-xs font-medium transition ${foco} ${motivo === s.situacao ? 'border-[color:var(--accent)] text-[color:var(--text-primary)]' : 'border-[color:var(--border)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'}`}
                    >
                      {s.rotulo}
                      <span className="tabular-nums text-[color:var(--text-primary)]">{s.quantidade}</span>
                    </button>
                  ))}
                </div>
              </div>

              {visiveis.length === 0 ? (
                <p className="p-6 text-sm text-[color:var(--text-muted)]">
                  {busca.trim() ? `Nenhum recebimento encontrado para “${busca.trim()}”.` : 'Nenhum recebimento neste recorte.'}
                </p>
              ) : (
                <Tabela linhas={visiveis.slice((pagina - 1) * porPagina, pagina * porPagina)} gerou={aba === 'gerou'} onAbrir={setAberta} />
              )}
            </section>
            {/* 10 é a menor opção do seletor: acima disso o controle fica, para não sumir ao escolher 10. */}
            {visiveis.length > 10 && (
              <Pagination
                currentPage={pagina}
                totalPages={Math.ceil(visiveis.length / porPagina)}
                totalItems={visiveis.length}
                itemsPerPage={porPagina}
                onPageChange={(nova) => {
                  setPagina(nova);
                  // Quem pagina no rodapé volta ao começo da lista, não fica no fim da página.
                  if (listaRef.current?.getBoundingClientRect().top < 0) listaRef.current.scrollIntoView({ block: 'start' });
                }}
                onItemsPerPageChange={setPorPagina}
                itemLabel="recebimentos"
              />
            )}
            <p className="text-xs text-[color:var(--text-muted)]">
              Lido do IXC em {new Date(dados.gerado_em).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}. Conta 1% do valor pago de um título que já estava vencido quando a OS de cobrança abriu
              (renegociação: 1% do total, na primeira parcela paga) para quem finalizou a OS de cobrança interna aberta no pagamento.
            </p>
          </>
        )}
      </div>
      {/* Fora do `space-y-5`: a margem que ele dá ao último filho deslocaria o painel fixo. */}
      <Modal
        isOpen={confirmar}
        onClose={() => { if (!lancando) setConfirmar(false); }}
        title="Lançar comissão no IXC"
      >
        <p className="text-sm">
          Vai gravar no IXC <strong>{reais(totalMarcado)}</strong> de comissão de {mesPorExtenso(mes)}. Remover um lançamento depois só pelo IXC.
        </p>
        <fieldset className="mt-4" disabled={Boolean(lancando)}>
          <legend className="text-sm font-medium">Quem vai receber</legend>
          <ul className="mt-2 max-h-64 divide-y divide-[color:var(--border)] overflow-y-auto">
            {passiveis.map((c) => {
              const quantidade = c.por_situacao?.find((s) => s.situacao === 'A_LANCAR')?.quantidade || 0;
              return (
                <li key={c.id}>
                  <label className="flex min-h-11 cursor-pointer items-center gap-3 py-2 text-sm">
                    <input type="checkbox" checked={marcados.includes(c.id)} onChange={() => alternar(c.id)} className="h-4 w-4" />
                    <span className="min-w-0 flex-1 break-words">{pessoa(c.nome)}</span>
                    <span className="whitespace-nowrap text-right tabular-nums">
                      <span className="block font-semibold">{reais(c.comissao_a_lancar)}</span>
                      <span className="block text-xs text-[color:var(--text-muted)]">{quantidade} {quantidade === 1 ? 'recebimento' : 'recebimentos'}</span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </fieldset>
        {lancando && <p role="status" className="mt-3 text-sm tabular-nums">Lançando: {lancando.lancadas} lançadas, {reais(lancando.valor)}. Não feche a página.</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => setConfirmar(false)} disabled={Boolean(lancando)} className={botaoSecundario}>Cancelar</button>
          <button type="button" onClick={lancar} disabled={Boolean(lancando) || marcados.length === 0} className={botaoSecundario}>{lancando ? 'Lançando' : 'Confirmar lançamento'}</button>
        </div>
      </Modal>
      <Modal
        isOpen={Boolean(pedirModelo)}
        onClose={() => setPedirModelo(null)}
        title={pedirModelo === 'pdf' ? 'Baixar PDF' : 'Baixar XLSX'}
      >
        <fieldset>
          <legend className="text-sm text-[color:var(--text-muted)]">Qual versão de {mesPorExtenso(mes)}?</legend>
          <div className="mt-3 space-y-2">
            {MODELOS.map((opcao) => (
              <label
                key={opcao.valor}
                className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm transition ${modelo === opcao.valor
                  ? 'border-[color:var(--accent)] bg-[color:var(--surface-muted)]'
                  : 'border-[color:var(--border)] hover:bg-[color:var(--surface-muted)]'}`}
              >
                <input type="radio" name="modelo-exportacao" value={opcao.valor} checked={modelo === opcao.valor} onChange={() => setModelo(opcao.valor)} className="mt-0.5 h-4 w-4" />
                <span>
                  <span className="block font-medium text-[color:var(--text-primary)]">{opcao.titulo}</span>
                  <span className="block text-[color:var(--text-muted)]">{opcao.descricao}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => setPedirModelo(null)} className={botaoSecundario}>Cancelar</button>
          <button type="button" onClick={() => baixar(pedirModelo, modelo)} className={botaoSecundario}>
            {pedirModelo === 'pdf' ? <FileText className="h-4 w-4" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}
            Baixar
          </button>
        </div>
      </Modal>
      {aberta && <Painel linha={aberta} onFechar={() => setAberta(null)} />}
    </>
  );
};

const Resumo = ({ resumo, mes, titulo, grupo, onEscolherGrupo }) => {
  const recuperacao = resumo.recuperacao || {};
  const recuperado = GRUPOS.reduce((total, g) => total + (recuperacao[g.chave]?.valor || 0), 0);

  return (
    <section className={`${caixa} overflow-hidden`} aria-label="Resumo da comissão">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[color:var(--border)] px-5 py-3">
        <h2 className="text-base font-semibold text-[color:var(--text-primary)]">Resultado de {mesPorExtenso(mes)}</h2>
        <p className="max-w-full break-words text-sm text-[color:var(--text-muted)]">{titulo}</p>
      </div>
      <div className="grid divide-y divide-[color:var(--border)] lg:grid-cols-[1.2fr_1fr] lg:divide-x lg:divide-y-0">
        <div className="p-5 sm:p-6">
          <dl>
            <dt className="text-sm font-medium text-[color:var(--text-muted)]">Valor recuperado</dt>
            <dd className="mt-2 break-words text-3xl font-semibold tracking-tight tabular-nums text-[color:var(--text-primary)] sm:text-4xl">{reais(recuperado)}</dd>
          </dl>
          <p className="mt-2 text-sm text-[color:var(--text-muted)]"><span className="font-semibold tabular-nums text-[color:var(--text-primary)]">{resumo.recebimentos}</span> recebimentos que passaram pela cobrança</p>
        </div>
        <div className="p-5 sm:p-6">
          <dl className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <dt className="text-sm font-medium text-[color:var(--text-muted)]">Comissão gerada</dt>
            <dd className="text-2xl font-semibold tracking-tight tabular-nums text-[color:var(--text-primary)]">{reais(resumo.comissao)}</dd>
          </dl>
          <p className="mt-2 text-sm text-[color:var(--text-muted)]">{resumo.geraram} de {resumo.recebimentos} recebimentos geraram comissão</p>
          <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-[color:var(--border)] pt-3">
            <div><dt className="text-xs text-[color:var(--text-muted)]">Já lançada no IXC</dt><dd className="mt-1 font-semibold tabular-nums text-[color:var(--text-primary)]">{reais(resumo.comissao_lancada)}</dd></div>
            <div><dt className="text-xs text-[color:var(--text-muted)]">A lançar</dt><dd className="mt-1 font-semibold tabular-nums text-[color:var(--status-warning-strong)]">{reais(resumo.comissao_a_lancar)}</dd></div>
          </dl>
        </div>
      </div>
      <div className="border-t border-[color:var(--border)]">
        <div className="flex flex-wrap items-baseline justify-between gap-1 px-5 pt-4">
          <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Como os pagamentos foram recuperados</h3>
          <p className="text-xs text-[color:var(--text-muted)]">Selecione um grupo para conferir os recebimentos</p>
        </div>
        <div className="grid gap-1 p-3 md:grid-cols-3">
          {GRUPOS.map((g) => {
            const valores = recuperacao[g.chave] || { valor: 0, quantidade: 0 };
            return (
              <button key={g.chave} type="button" aria-pressed={grupo === g.chave} onClick={() => onEscolherGrupo(g.chave)} className={`min-w-0 rounded-lg p-3 text-left ${foco} hover:bg-[color:var(--surface-muted)] ${grupo === g.chave ? 'bg-[color:var(--surface-muted)] ring-1 ring-inset ring-[color:var(--accent)]' : ''}`}>
                <span className="flex items-center gap-2 text-xs font-medium text-[color:var(--text-primary)]"><span className="h-2 w-2 shrink-0 rounded-sm" style={{ backgroundColor: g.cor }} aria-hidden="true" />{g.rotulo}</span>
                <span className="mt-2 block text-lg font-semibold tabular-nums text-[color:var(--text-primary)]">{reais(valores.valor)}</span>
                <span className="mt-1 block text-xs tabular-nums text-[color:var(--text-muted)]">{valores.quantidade} {valores.quantidade === 1 ? 'recebimento' : 'recebimentos'}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/**
 * Quanto cada pessoa recuperou em R$: o valor pago só dos recebimentos que geraram comissão para
 * ela, validados como cobrança efetiva. É o valor absoluto, e não a comissão, porque o percentual
 * pode mudar e a comparação entre meses e pessoas não pode depender dele.
 */
const Cobradores = ({ cobradores, selecionado, onEscolher }) => {
  const recuperado = (c) => c.valor_recebido || 0;
  const comValor = cobradores.filter((c) => recuperado(c) > 0).sort((a, b) => recuperado(b) - recuperado(a));
  const outros = cobradores.length - comValor.length;
  const maior = Math.max(...comValor.map(recuperado), 0.01);

  return (
    <section className={`${caixa} p-5`} aria-labelledby="titulo-cobradores">
      <h3 id="titulo-cobradores" className="text-sm font-semibold text-[color:var(--text-primary)]">Recuperado por cobrador</h3>
      <p className="text-xs text-[color:var(--text-muted)]">Valor pago dos recebimentos que geraram comissão para a pessoa</p>
      <ul className="mt-3 max-h-80 overflow-y-auto divide-y divide-[color:var(--border)]">
        {comValor.map((c) => (
          <li key={c.id}>
            <button type="button" aria-pressed={String(selecionado) === String(c.id)} onClick={() => onEscolher(c.id)} className={`grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 rounded-md py-2.5 text-left transition hover:bg-[color:var(--surface-muted)] sm:px-2 ${foco}`}>
              <span className="col-span-2 break-words text-sm font-medium text-[color:var(--text-primary)]">{pessoa(c.nome)}</span>
              <span className="order-3 col-span-2 flex h-2 overflow-hidden rounded-full bg-[color:var(--surface-muted)] " aria-hidden="true">
                <span className="bg-[color:var(--serie-1)]" style={{ width: `${(recuperado(c) / maior) * 100}%` }} />
              </span>
              <span className="col-span-2 flex flex-wrap items-baseline justify-between gap-x-3 text-left">
                <span className="block text-sm font-semibold tabular-nums text-[color:var(--text-primary)]">{reais(recuperado(c))}</span>
                <span className="block text-xs tabular-nums text-[color:var(--text-muted)]">
                  {c.geraram} {c.geraram === 1 ? 'recebimento' : 'recebimentos'} · comissão {reais(c.comissao)}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
      {outros > 0 && (
        <p className="mt-2 text-xs text-[color:var(--text-muted)]">
          {outros === 1 ? 'Mais 1 pessoa' : `Mais ${outros} pessoas`} sem recebimentos que geraram comissão; escolha no seletor de cobrador para ver.
        </p>
      )}
    </section>
  );
};

const Tabela = ({ linhas, gerou, onAbrir }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-[color:var(--surface-muted)] text-left text-xs text-[color:var(--text-muted)]">
        <tr>
          <th scope="col" className="px-4 py-2.5 font-medium">Cliente</th>
          <th scope="col" className="px-4 py-2.5 font-medium">Pagamento</th>
          <th scope="col" className="hidden px-4 py-2.5 text-right font-medium md:table-cell">Valor pago</th>
          <th scope="col" className="hidden px-4 py-2.5 font-medium lg:table-cell">{gerou ? 'Cobrador' : 'Responsável'}</th>
          <th scope="col" className="px-4 py-2.5 font-medium">{gerou ? 'Situação' : 'Motivo'}</th>
          {gerou && <th scope="col" className="px-4 py-2.5 text-right font-medium">Comissão</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-[color:var(--border)]">
        {linhas.map((l) => (
          <tr key={l.origem} onClick={(e) => onAbrir(l, e)} className="cursor-pointer transition hover:bg-[color:var(--surface-muted)]">
            <td className="max-w-[16rem] px-4 py-2.5">
              <button type="button" onClick={(e) => { e.stopPropagation(); onAbrir(l); }} className={`block max-w-full truncate rounded text-left font-medium text-[color:var(--text-primary)] underline-offset-4 hover:underline ${foco}`}>
                {pessoa(l.cliente)}
              </button>
              <span className="block text-xs text-[color:var(--text-muted)]">
                Contrato {l.contrato_id || '—'}{l.tipo === 'renegociacao' ? ' · renegociação' : ''}
              </span>
            </td>
            <td className="whitespace-nowrap px-4 py-2.5">
              <span className="block tabular-nums text-[color:var(--text-primary)]">{dia(l.pagamento)}</span>
              <span className="block text-xs tabular-nums text-[color:var(--text-muted)]">{l.dias_atraso} dias de atraso</span>
            </td>
            <td className="hidden whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-[color:var(--text-primary)] md:table-cell">{reais(l.valor_pago)}</td>
            <td className="hidden max-w-[12rem] truncate px-4 py-2.5 text-[color:var(--text-primary)] lg:table-cell">{pessoa(l.cobrador)}</td>
            <td className="px-4 py-2.5"><Chip situacao={l.situacao} /></td>
            {gerou && <td className="whitespace-nowrap px-4 py-2.5 text-right font-semibold tabular-nums text-[color:var(--text-primary)]">{reais(l.comissao)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Painel = ({ linha, onFechar }) => {
  const [historico, setHistorico] = useState(null);
  const [falhou, setFalhou] = useState(false);
  const fechar = useRef(null);

  useEffect(() => {
    const anterior = document.activeElement;
    fechar.current?.focus();
    const tecla = (e) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', tecla);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', tecla);
      document.body.style.overflow = '';
      anterior?.focus?.();
    };
  }, [onFechar]);

  useEffect(() => {
    const ids = [linha.os_id, linha.os_externa_anterior, linha.os_externa_aberta].filter(Boolean);
    if (ids.length === 0) { setHistorico([]); return undefined; }
    let vigente = true;
    api.get('/cobranca/comissao/historico', { params: { os: ids.join(',') } })
      .then((r) => { if (vigente) setHistorico(r.data?.data || []); })
      .catch(() => { if (vigente) { setHistorico([]); setFalhou(true); } });
    return () => { vigente = false; };
  }, [linha]);

  const s = SITUACAO[linha.situacao] || {};
  const fatos = [
    ['Valor pago', reais(linha.valor_pago)],
    ['Atraso no pagamento', `${linha.dias_atraso} dias`],
    ['OS de cobrança', linha.os_id ? `${linha.os_id}` : 'nenhuma aberta'],
    [linha.gerou ? 'Comissionou' : 'Responsável', pessoa(linha.cobrador)],
  ];

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="titulo-painel">
      <button type="button" tabIndex={-1} aria-hidden="true" onClick={onFechar} className="absolute inset-0 h-full w-full cursor-default bg-black/40" />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-xl flex-col overflow-y-auto border-l border-[color:var(--border)] bg-[color:var(--surface-strong)] shadow-xl">
        <header className="flex items-start justify-between gap-4 border-b border-[color:var(--border)] p-5">
          <div className="min-w-0">
            <h2 id="titulo-painel" className="text-lg font-semibold text-[color:var(--text-primary)]">{pessoa(linha.cliente)}</h2>
            <p className="text-sm text-[color:var(--text-muted)]">
              Cliente {linha.cliente_id} · Contrato {linha.contrato_id || '—'} · {linha.tipo === 'renegociacao' ? `Renegociação ${linha.renegociacao_id}` : `Boleto ${linha.boleto_id}`}
            </p>
          </div>
          <button ref={fechar} type="button" onClick={onFechar} aria-label="Fechar" className={`rounded-md p-2 text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)] hover:text-[color:var(--text-primary)] ${foco}`}>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </header>

        <div className="space-y-6 p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Chip situacao={linha.situacao} />
            {linha.gerou && <p className="text-2xl font-semibold tabular-nums text-[color:var(--status-good-strong)]">{reais(linha.comissao)}</p>}
          </div>
          <p className="text-sm text-[color:var(--text-primary)]">
            {s.explica}
            {linha.situacao === 'COMISSAO_DIVERGENTE' && ` O IXC tem ${linha.comissoes_ixc} ${linha.comissoes_ixc === 1 ? 'lançamento' : 'lançamentos'} somando ${reais(linha.valor_comissao_ixc)}.`}
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
            {fatos.map(([rotulo, valor]) => (
              <div key={rotulo}>
                <dt className="text-xs text-[color:var(--text-muted)]">{rotulo}</dt>
                <dd className="text-sm font-medium tabular-nums text-[color:var(--text-primary)]">{valor}</dd>
              </div>
            ))}
          </dl>

          <section aria-labelledby="titulo-regua">
            <h3 id="titulo-regua" className="mb-3 text-sm font-semibold text-[color:var(--text-primary)]">Régua da cobrança</h3>
            <ReguaDaCobranca linha={linha} historico={historico} carregando={historico === null} />
            {falhou && <p className="mt-3 text-xs text-[color:var(--status-danger-strong)]">O histórico da OS não carregou; a régua mostra só as datas do boleto.</p>}
          </section>
        </div>
      </aside>
    </div>
  );
};

export default ComissaoCobranca;
